import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    useMediaQuery,
} from '@mui/material';

import { getZoneMediaItems } from 'shared/loadZoneSlice';
import { sortBy } from 'GeminiViewerComponent/helpers/lodashUtils';
import { NewsFlashAccordian } from './components/NewsFlashAccordian';
import { LinkAccordion } from '../LinkAccordion';
import { getConfig } from 'GeminiViewerComponent/features/config/configSlice';
import { getAllTags, selectGroups, zoneNewsFlashes } from 'GeminiViewerComponent/features/asset/assetSlice';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { selectContentPanelItem } from 'GeminiViewerComponent/features/contentPanel/contentPanelItemSlice';
import { getTagFilters } from 'GeminiViewerComponent/features/globals/visibilitySlice';
import { GroupOption } from '../SidePanel';
import getLinkIcon from 'GeminiViewerComponent/helpers/getLinkIcon';

const useStyles = makeStyles((theme) => ({
    noLinks: {
        textAlign: 'center',
        margin: '25px',
        fontSize: theme.typography.pxToRem(20),
        color: (props) => props.colors.secondarySectionForeground,
    },
}));

const SidePanelBody = ({ linkTypes, audienceIds, groupOption }) => {
    const theme = useSelector(selectActiveTheme);
    const classes = useStyles(theme);
    const [links, setLinks] = useState(new Map());
    const [open, setOpen] = useState(false);
    const config = useSelector(getConfig);
    const panelItem = useSelector(selectContentPanelItem);
    const zoneMediaItems = useSelector(getZoneMediaItems);
    const zoneNewsFlashIds = useSelector(zoneNewsFlashes);
    const tagFilters = useSelector(getTagFilters);
    const allTags = useSelector(getAllTags);
    const groups = useSelector(selectGroups);
    const muiTheme = useTheme();
    const fullScreen = useMediaQuery(muiTheme.breakpoints.down('md'));
    const defaultGroup = { tag_id: -1, name: groupOption === GroupOption.TAG ? 'Untagged' : 'Ungrouped' };

    useEffect(() => {
        const linkMap = new Map();
        const allLinks = Object.keys(panelItem ?? {}).length > 0 ? panelItem : { links: zoneMediaItems };
        const links = Object.values(allLinks.links ?? {}).sort(sortBy('display_name', 'DESC'));

        links.forEach((link) => {
            if (!link.is_active) return;

            let hasAllAudiences = true;
            if (audienceIds && audienceIds.length > 0 && link.audience_ids && link.audience_ids.length > 0) {
                hasAllAudiences = link.audience_ids.some((audienceId) => {
                    return audienceIds.includes(audienceId);
                });
            }
            if (hasAllAudiences) {
                if (groupOption === GroupOption.TYPE) {
                    const linkTypeId = link.link_type ? link.link_type.link_type_id : link.link_type_id;
                    if (!linkMap.has(linkTypeId)) {
                        linkMap.set(linkTypeId, []);
                    }
                    linkMap.get(linkTypeId).push(link);
                } else {
                    let added = false;
                    if (Array.isArray(link.content?.tag_ids)) {
                        for (const tagId of link.content.tag_ids) {
                            const tag = allTags.find((t) => t.tag_id === tagId);
                            if (!tag) continue;

                            if (groupOption === GroupOption.TAG_GROUP) {
                                if (tag.group_ids?.length > 0) {
                                    for (const groupId of tag.group_ids) {
                                        const group = groups.find((g) => g.group_id === groupId);
                                        if (!group) continue;
                                        if (!linkMap.has(group)) {
                                            linkMap.set(group, []);
                                        }
                                        // another tag may have the same group
                                        if (!linkMap.get(group).includes(link)) {
                                            linkMap.get(group).push(link);
                                            added = true;
                                        }
                                    }
                                }
                            } else {
                                if (!linkMap.has(tag)) {
                                    linkMap.set(tag, []);
                                }
                                linkMap.get(tag).push(link);
                                added = true;
                            }
                        }
                    }

                    if (!added) {
                        if (!linkMap.has(defaultGroup)) {
                            linkMap.set(defaultGroup, []);
                        }
                        linkMap.get(defaultGroup).push(link);
                    }
                }
            }
        });

        setLinks(linkMap);
    }, [panelItem, zoneMediaItems, audienceIds, groupOption]);

    if (links.size === 0) {
        return (
            <div>
                {!Object.keys(panelItem ?? {}).length > 0 &&
                    zoneNewsFlashIds.length > 0 &&
                    config.modeType === 'viewer' && <NewsFlashAccordian />}
                <div className={classes.noLinks}>
                    {tagFilters.item_tags.length === 0 && tagFilters.zone_tags.length === 0
                        ? 'No links available.'
                        : 'No links available. Check your zone or item tag filters.'}
                </div>
            </div>
        );
    }

    const closeModal = () => {
        setOpen(false);
    };

    return (
        <div>
            {!Object.keys(panelItem ?? {}).length > 0 &&
                zoneNewsFlashIds.length > 0 &&
                config.modeType === 'viewer' && <NewsFlashAccordian />}
            {Array.from(links.keys())
                .map((key) =>
                    key.tag_id
                        ? [
                              key.tag_id,
                              key.name ?? '',
                              key.image_url && <img key={key.tag_id} src={key.image_url} />,
                              links.get(key),
                          ]
                        : key.group_id
                        ? [key.group_id, key.display_name ?? '', null, links.get(key)]
                        : [key, linkTypes[key].label, getLinkIcon(key), links.get(key)]
                )
                .sort((a, b) =>
                    a[0] === defaultGroup.tag_id ? 1 : b[0] === defaultGroup.tag_id ? -1 : a[1].localeCompare(b[1])
                )
                .map(([id, label, icon, links]) => {
                    return (
                        <LinkAccordion
                            headerLabel={label}
                            headerIcon={icon}
                            linkTypes={linkTypes}
                            links={links}
                            key={id}
                            isZoneMedia={!Object.keys(panelItem ?? {}).length > 0}
                            setOpen={setOpen}
                        />
                    );
                })}
            <Dialog fullScreen={fullScreen} open={open}>
                <DialogTitle>Warning</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The link you selected is configured to display in a Companion App window, but there is no
                        Companion App window open.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export { SidePanelBody };
